import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { App } from 'antd';
import { useOutboundOrderDetailsQuery } from 'api/warehouse';
import type { UploadFile } from 'antd';
import { WAREHOUSE_ROUTES } from '../../../constants/routes';

interface ItemResponse {
  id: string;
  sku: string;
  status: string;
  quantity: number;
  description?: string;
  attachments: any[];
  purchaseOrderId: string | null;
  purchaseOrderItemId: string | null;
}

interface OrderDetails {
  id: string;
  uuid: string;
  clientName?: string;
  clientRequestNumber?: string;
  status: string;
  itemsResponseList: ItemResponse[];
}

export const useOutboundOrderDetails = () => {
  const { message } = App.useApp();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [fileListByItem, setFileListByItem] = useState<{ [key: string]: UploadFile[] }>({});

  const {
    data: orderResponse,
    isLoading,
    error,
    refetch,
  } = useOutboundOrderDetailsQuery(id || '', {
    skip: !id,
  });

  const order: OrderDetails | undefined = orderResponse?.data;

  useEffect(() => {
    if (error) {
      message.error('Failed to load order details');
      navigate(WAREHOUSE_ROUTES.outboundPackages);
    }
  }, [error, navigate, message]);

  // Map attachments to UploadFile format for each item
  useEffect(() => {
    if (order?.itemsResponseList) {
      const newFileListByItem: { [key: string]: UploadFile[] } = {};

      order.itemsResponseList.forEach((item) => {
        if (item.attachments && item.attachments.length > 0) {
          newFileListByItem[item.id] = item.attachments.map((attachment: any, index: number) => ({
            uid: attachment.id || `${item.id}-${index}`,
            name: attachment.fileName || `image-${index + 1}`,
            status: 'done' as const,
            url: attachment.files?.ATTACHMENTS,
            attachmentId: attachment.id,
            attachmentType: attachment.attachmentType, // Preserve attachment type
          }));
        }
      });

      setFileListByItem(newFileListByItem);
    }
  }, [order]);

  const handlePreview = useCallback((file: UploadFile) => {
    setPreviewImage(file.url || '');
    setPreviewOpen(true);
  }, []);

  const handleGoToSource = useCallback(
    (item: ItemResponse) => {
      // Navigate to the inbound order details with the specific item
      if (!item.purchaseOrderId) {
        message.error('Source order information not available');
        return;
      }

      // Navigate to inbound order details with item auto-expanded
      navigate(`/warehouse/inbound/${item.purchaseOrderId}`, {
        state: {
          expandedItemId: item.purchaseOrderItemId,
        },
      });
    },
    [navigate, message]
  );

  const handleBack = useCallback(() => {
    navigate(WAREHOUSE_ROUTES.outboundPackages);
  }, [navigate]);

  return {
    order,
    isLoading,
    fileListByItem,
    previewOpen,
    previewImage,
    setPreviewOpen,
    setPreviewImage,
    handlePreview,
    handleGoToSource,
    handleBack,
    refetch,
  };
};
